import React, { useEffect, useState, useRef } from "react";
import io from "socket.io-client";
import { postData } from "../../../fetchRoutes/postData";

const backend = process.env.REACT_APP_BACKEND;
const socket = io(backend, { transports: ["websocket"] });

export default function BuyerChat({ sellerId, buyerId, onClose }) {
  const chatUrl = process.env.REACT_APP_CHAT;

  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const chatEndRef = useRef(null);
  const [queueStatus, setQueueStatus] = useState(null);

  const stringifiedBuyerId = String(buyerId);
  const stringifiedSellerId = String(sellerId);
  const roomName = [stringifiedBuyerId, stringifiedSellerId].sort().join("-");

  useEffect(() => {
    if (!stringifiedBuyerId || !stringifiedSellerId) return;

    const fetchOrCreateChat = async () => {
      const res = await postData(`${chatUrl}/${stringifiedBuyerId}/${stringifiedSellerId}`);
      if (res.status === "Success") {
        setChatId(res.chatId);
        setMessages(res.chat.messages || []);
      }
    };

    fetchOrCreateChat();
  }, [chatUrl, stringifiedBuyerId, stringifiedSellerId]);

  useEffect(() => {
    socket.emit("joinRoom", { buyerId: stringifiedBuyerId, sellerId: stringifiedSellerId }, (response) => {
      if (!response.success && response.message) {
        setQueueStatus(response.message);
      }
    });

    const handleMessage = (msg) => {
      setMessages((prev) => {
        const index = prev.findIndex(
          (m) => m._local && m.text === msg.text && m.senderId === msg.senderId
        );
    
        if (index !== -1) {
          // Replace the local message with the one from the server
          const updatedMessages = [...prev];
          updatedMessages[index] = msg;
          return updatedMessages;
        }
    
        // Only add if it's not already in the list
        const isDuplicate = prev.some(
          (m) => m.text === msg.text && m.senderId === msg.senderId
        );
        return isDuplicate ? prev : [...prev, msg];
      });
    };
    

    socket.on("receiveMessage", handleMessage);

    socket.on("startChat", (data) => {
      if (data.success) {
        setChatId(data.chatId);
        socket.emit("joinRoom", { buyerId: stringifiedBuyerId, sellerId: stringifiedSellerId });
        setQueueStatus(null); // Clear queue status once chat starts
      }
    });

    return () => {
      socket.off("receiveMessage", handleMessage);
      socket.off("startChat");
    };
  }, [stringifiedBuyerId, stringifiedSellerId]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = () => {
    if (message.trim() && chatId) {
      const newMessage = {
        senderId: stringifiedBuyerId,
        text: message,
        _local: true, // local flag
      };

      // Immediately show the message in UI
      setMessages((prevMessages) => [...prevMessages, newMessage]);

      // Emit to server (without _local)
      socket.emit("sendMessage", {
        message: { ...newMessage, _local: undefined },
        room: roomName,
        senderId: stringifiedBuyerId,
        senderType: "Buyer",
        chatId,
      });

      setMessage("");
    }
  };

  const handleChange = (event) => {
    setMessage(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chatContainer">
      <div className="chatTitle">
        <span className="chatSeller">Chat with Seller</span>
        <button onClick={onClose} className="closeChat">×</button>
      </div>
      {/* Chat message */}
      <div className="chatBox">
        {queueStatus && <div className="queueStatus">{queueStatus}</div>}
        {messages.map((msg, index) => (
          <div key={index} className={`messageBox ${msg.senderId === stringifiedBuyerId ? "sent" : "received"}`}>
            <span><strong>{msg.senderId === stringifiedBuyerId ? "You" : "Seller"}:</strong> {msg.text}</span>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      <div className="chatMessageInput">
        <input
          className="fullName messageInput"
          type="text"
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter your message here"
        />
        <button onClick={sendMessage} className="sendMessage chat"></button>
      </div>
    </div>
  );
}
